import React, { useState } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonModal,
  IonIcon
} from '@ionic/react';
import {
  star,
  ribbon,
  lockClosed,
  checkmarkCircle,
  closeCircle,
  shareSocial,
  chevronDown,
  chevronUp
} from 'ionicons/icons';
import { useConfig } from '../hooks/useConfig';
import { useAchievements } from '../hooks/useAchievements';
import { storageService } from '../services/StorageService';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Achievements.css';

/**
 * Achievements Page - Beautiful Redesign
 * Shows all badges and achievements with gamification elements
 */
function Achievements() {
  const { achievements, config } = useConfig();
  const { unlockedBadges, stats } = useAchievements();
  const baseMeta = seoService.getPageMeta('home');
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    legendary: true,
    epic: true,
    rare: true,
    common: true
  });

  // Get total points from storage or stats
  const totalPoints = storageService.get('totalPoints') || stats?.totalPoints || 0;
  
  // Get unlocked badge IDs (handle both array of objects and array of IDs)
  const unlockedBadgeIds = unlockedBadges?.map(b => typeof b === 'string' ? b : b.id) || [];

  const allBadges = achievements?.badges || [];
  const unlockedCount = unlockedBadgeIds.length;
  const totalCount = allBadges.length;
  const progressPercent = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  // Group badges by rarity
  const badgesByRarity = {
    legendary: allBadges.filter(b => b.rarity === 'legendary'),
    epic: allBadges.filter(b => b.rarity === 'epic'),
    rare: allBadges.filter(b => b.rarity === 'rare'),
    common: allBadges.filter(b => b.rarity === 'common' || !b.rarity)
  };

  const isBadgeUnlocked = (badgeId) => {
    return unlockedBadgeIds.includes(badgeId);
  };

  const getUnlockedCountByRarity = (rarity) => {
    return badgesByRarity[rarity].filter(b => isBadgeUnlocked(b.id)).length;
  };

  const toggleSection = (rarity) => {
    setExpandedSections(prev => ({
      ...prev,
      [rarity]: !prev[rarity]
    }));
  };

  const rarityConfig = {
    legendary: {
      icon: '⭐',
      label: 'Legendary',
      color: '#FFD700',
      gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      glowColor: 'rgba(255, 215, 0, 0.5)'
    },
    epic: {
      icon: '💜',
      label: 'Epic',
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 50%, #7C4DFF 100%)',
      glowColor: 'rgba(156, 39, 176, 0.5)'
    },
    rare: {
      icon: '💙',
      label: 'Rare',
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #03A9F4 50%, #00BCD4 100%)',
      glowColor: 'rgba(33, 150, 243, 0.5)'
    },
    common: {
      icon: '⬜',
      label: 'Common',
      color: '#78909C',
      gradient: 'linear-gradient(135deg, #90A4AE 0%, #78909C 50%, #607D8B 100%)',
      glowColor: 'rgba(120, 144, 156, 0.3)'
    }
  };

  // Calculate user level based on points
  const levels = config?.gamification?.levels || [
    { level: 1, name: 'Beginner', points: 0, icon: '🌱' },
    { level: 2, name: 'Learner', points: 500, icon: '🌿' },
    { level: 3, name: 'Coder', points: 1500, icon: '🌳' },
    { level: 4, name: 'Expert', points: 5000, icon: '🏆' },
    { level: 5, name: 'Master', points: 10000, icon: '👑' }
  ];

  const currentLevel = levels.reduce((acc, level) => {
    if ((totalPoints || 0) >= level.points) return level;
    return acc;
  }, levels[0]);

  // Emoji fallback for badges when images fail to load
  const getBadgeEmoji = (badgeId) => {
    const emojiMap = {
      'first-code': '💻',
      '10-lessons': '📚',
      '25-lessons': '🎓',
      '50-lessons': '🏅',
      '7-day-streak': '🔥',
      '30-day-streak': '⚡',
      '100-day-streak': '👑',
      'challenge-solver': '🧩',
      'code-master': '🎯',
      'perfect-score': '💯',
      'speed-demon': '⚡',
      'level-master': '🌟',
      'poet-coder': '✍️',
      'interview-ready': '💼'
    };
    return emojiMap[badgeId] || '🏆';
  };

  return (
    <>
      <SEO
        title="Achievements - Your Badges & Progress"
        description="View your earned badges, achievements, and learning milestones."
        ogImage={baseMeta.ogImage}
      />
      <IonPage className="achievements-page">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Achievements</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="achievements-content">
        {/* Hero Section */}
        <div className="achievements-hero">
          <div className="hero-background">
            <div className="sparkle sparkle-1">✨</div>
            <div className="sparkle sparkle-2">⭐</div>
            <div className="sparkle sparkle-3">✨</div>
            <div className="sparkle sparkle-4">💫</div>
          </div>
          
          <div className="hero-content">
            <div className="trophy-container">
              <div className="trophy-glow"></div>
              <div className="trophy-icon">🏆</div>
              <div className="trophy-ring"></div>
            </div>
            
            <h1 className="hero-title">Your Achievements</h1>
            <p className="hero-subtitle">Collect badges and become a coding master!</p>
          </div>

          {/* Circular Progress */}
          <div className="progress-circle-container">
            <svg className="progress-circle" viewBox="0 0 120 120">
              <circle 
                className="progress-bg" 
                cx="60" cy="60" r="52" 
                fill="none" 
                strokeWidth="8"
              />
              <circle 
                className="progress-fill" 
                cx="60" cy="60" r="52" 
                fill="none" 
                strokeWidth="8"
                strokeDasharray={`${progressPercent * 3.27} 327`}
                strokeLinecap="round"
              />
            </svg>
            <div className="progress-text">
              <span className="progress-percent">{progressPercent}%</span>
              <span className="progress-label">Complete</span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon unlocked">
              <IonIcon icon={checkmarkCircle} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{unlockedCount}</span>
              <span className="stat-label">Unlocked</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon total">
              <IonIcon icon={ribbon} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{totalCount}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon points">
              <IonIcon icon={star} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{totalPoints || 0}</span>
              <span className="stat-label">Points</span>
            </div>
          </div>
          
          <div className="stat-card level-card">
            <div className="level-icon">{currentLevel.icon}</div>
            <div className="stat-info">
              <span className="stat-value">{currentLevel.name}</span>
              <span className="stat-label">Level {currentLevel.level}</span>
            </div>
          </div>
        </div>

        {/* Badge Sections by Rarity */}
        <div className="badges-container">
          {['legendary', 'epic', 'rare', 'common'].map(rarity => {
            const badges = badgesByRarity[rarity];
            if (badges.length === 0) return null;
            
            const rarityInfo = rarityConfig[rarity];
            const unlockedInRarity = getUnlockedCountByRarity(rarity);
            
            return (
              <div key={rarity} className={`rarity-section ${rarity}`}>
                <button 
                  className="rarity-header"
                  onClick={() => toggleSection(rarity)}
                  style={{ '--rarity-color': rarityInfo.color }}
                >
                  <div className="rarity-title">
                    <span className="rarity-icon">{rarityInfo.icon}</span>
                    <span className="rarity-name">{rarityInfo.label}</span>
                    <span className="rarity-count">
                      {unlockedInRarity}/{badges.length}
                    </span>
                  </div>
                  <IonIcon 
                    icon={chevronDown} 
                    className={`expand-icon ${expandedSections[rarity] ? 'expanded' : ''}`}
                  />
                </button>
                
                {expandedSections[rarity] && (
                  <div className="badges-grid">
                    {badges.map((badge, index) => {
                      const unlocked = isBadgeUnlocked(badge.id);
                      return (
                        <div 
                          key={badge.id}
                          className={`badge-card ${rarity} ${unlocked ? 'unlocked' : 'locked'}`}
                          onClick={() => setSelectedBadge(badge)}
                          style={{ 
                            '--animation-delay': `${index * 0.1}s`,
                            '--rarity-gradient': rarityInfo.gradient,
                            '--rarity-glow': rarityInfo.glowColor
                          }}
                        >
                          <div className="badge-card-inner">
                            <div className="badge-image-wrapper">
                              {unlocked && <div className="badge-glow"></div>}
                              {unlocked && <div className="badge-shine"></div>}
                              <img 
                                src={`/assets/media/${badge.image}`} 
                                alt={badge.name}
                                className="badge-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="badge-emoji-fallback" style={{ display: 'none' }}>
                                {getBadgeEmoji(badge.id)}
                              </div>
                              {!unlocked && (
                                <div className="badge-lock-overlay">
                                  <IonIcon icon={lockClosed} />
                                </div>
                              )}
                            </div>
                            <div className="badge-info">
                              <span className="badge-name">{badge.name}</span>
                              {unlocked && badge.points && (
                                <span className="badge-points">+{badge.points} pts</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {allBadges.length === 0 && (
          <div className="empty-achievements">
            <div className="empty-icon">🎯</div>
            <h3>No Achievements Yet</h3>
            <p>Start learning to unlock amazing badges!</p>
          </div>
        )}
      </IonContent>

      {/* Badge Detail Modal */}
      <IonModal 
        isOpen={selectedBadge !== null} 
        onDidDismiss={() => setSelectedBadge(null)}
        className="badge-modal"
      >
        {selectedBadge && (
          <div className="badge-modal-content">
            <button 
              className="modal-close"
              onClick={() => setSelectedBadge(null)}
            >
              <IonIcon icon={closeCircle} />
            </button>
            
            <div 
              className={`modal-badge-display ${selectedBadge.rarity || 'common'} ${
                isBadgeUnlocked(selectedBadge.id) ? 'unlocked' : 'locked'
              }`}
              style={{ 
                '--rarity-gradient': rarityConfig[selectedBadge.rarity || 'common'].gradient,
                '--rarity-glow': rarityConfig[selectedBadge.rarity || 'common'].glowColor
              }}
            >
              <div className="modal-badge-glow"></div>
              <img 
                src={`/assets/media/${selectedBadge.image}`} 
                alt={selectedBadge.name}
                className="modal-badge-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="modal-emoji-fallback" style={{ display: 'none' }}>
                {getBadgeEmoji(selectedBadge.id)}
              </div>
              {!isBadgeUnlocked(selectedBadge.id) && (
                <div className="modal-lock-overlay">
                  <IonIcon icon={lockClosed} />
                </div>
              )}
            </div>
            
            <div className="modal-badge-info">
              <div className={`rarity-badge ${selectedBadge.rarity || 'common'}`}>
                {rarityConfig[selectedBadge.rarity || 'common'].icon} {rarityConfig[selectedBadge.rarity || 'common'].label}
              </div>
              
              <h2 className="modal-badge-name">{selectedBadge.name}</h2>
              <p className="modal-badge-description">{selectedBadge.description}</p>
              
              {selectedBadge.points && (
                <div className="modal-badge-points">
                  <IonIcon icon={star} />
                  <span>{selectedBadge.points} Points</span>
                </div>
              )}
              
              {isBadgeUnlocked(selectedBadge.id) ? (
                <div className="modal-status unlocked">
                  <IonIcon icon={checkmarkCircle} />
                  <span>Unlocked!</span>
                </div>
              ) : (
                <div className="modal-status locked">
                  <IonIcon icon={lockClosed} />
                  <span>Keep learning to unlock!</span>
                </div>
              )}
              
              {isBadgeUnlocked(selectedBadge.id) && (
                <button className="share-button">
                  <IonIcon icon={shareSocial} />
                  <span>Share Achievement</span>
                </button>
              )}
            </div>
          </div>
        )}
      </IonModal>
    </IonPage>
    </>
  );
}

export default Achievements;